import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { apiFetch, getAuthHeader, handleUnauthorized } from '../api/core'
import { useStore } from '../store'
import type { FormState, InjectionSpec, InjectionType, SetGenerationResult } from '../types/testdata.types'
import { buildDocNumber } from './useTestDataForm'

export const INJECTION_TYPES: InjectionType[] = [
  'price_deviation',
  'missing_invoice_line',
  'unknown_article',
  'partial_delivery',
  'overbilling',
  'wrong_vat_rate',
  'wrong_gln',
  'duplicate_invoice_number',
]

export function emptyInjection(type: InjectionType): InjectionSpec {
  return { injection_type: type, line_index: 0, price_delta_pct: 10, qty_delta: 1, vat_rate: null, wrong_gln: null }
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mimeType })
}

interface UseErrorInjectionOptions {
  buildRequestBody: () => Record<string, unknown>
  validateForm: () => boolean
  form: FormState
  setForm: React.Dispatch<React.SetStateAction<FormState>>
  refreshDocCounter: () => void
  ensureCurrentAutoDocumentNumber: (currentVisibleValue: string) => Promise<string>
}

export function useErrorInjection(opts: UseErrorInjectionOptions) {
  const { t } = useTranslation()
  const [enabled, setEnabledRaw] = useState(false)
  const [injections, setInjections] = useState<InjectionSpec[]>([])
  const [result, setResult] = useState<SetGenerationResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const prefillSetNumbers = (force = false) => {
    const initials = useStore.getState().currentUser?.initials ?? ''
    const fetchNumber = (docType: 'order_response' | 'despatch_advice', key: 'order_response_number' | 'desadv_number') => {
      apiFetch<{ counter: number }>(`/api/doc-counters/${docType}`)
        .then(data => {
          opts.setForm(f => f[key] ? f : { ...f, [key]: buildDocNumber(docType, initials, data.counter) })
        })
        .catch(() => { /* gebruiker vult handmatig in */ })
    }
    if (force || !opts.form.order_response_number) fetchNumber('order_response', 'order_response_number')
    if (force || !opts.form.desadv_number) fetchNumber('despatch_advice', 'desadv_number')
  }

  const setEnabled = (value: boolean) => {
    setEnabledRaw(value)
    setError(null)
    if (value) prefillSetNumbers()
  }

  const addInjection = (type: InjectionType) =>
    setInjections(list => [...list, emptyInjection(type)])

  const removeInjection = (idx: number) =>
    setInjections(list => list.filter((_, i) => i !== idx))

  const updateInjection = (idx: number, patch: Partial<InjectionSpec>) =>
    setInjections(list => list.map((inj, i) => i === idx ? { ...inj, ...patch } : inj))

  const generateSet = async () => {
    setError(null)
    setResult(null)
    if (!opts.validateForm()) return
    if (!opts.form.order_response_number.trim() || !opts.form.desadv_number.trim()) {
      setError(t('error_injection.error_set_numbers_required'))
      return
    }

    setLoading(true)
    try {
      // Zie useDocNumber.ensureCurrentAutoDocumentNumber: bij een auto-managed
      // nummer vlak vóór submit hersynct met de server; bij user-managed
      // ongewijzigd (geen fetch). Gebruik de retourwaarde rechtstreeks in de
      // payload, niet opts.form.document_number ná deze await.
      const freshInvoiceNumber = await opts.ensureCurrentAutoDocumentNumber(opts.form.document_number)
      const body = {
        ...opts.buildRequestBody(),
        document_type: 'invoice',
        document_number: freshInvoiceNumber,
        xml_format: 'sales005',
        injections,
      }
      const res = await fetch('/api/testdata/generate-set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getAuthHeader() },
        body: JSON.stringify(body),
      })
      handleUnauthorized(res)
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }))
        throw new Error(err.detail ?? res.statusText)
      }
      const data: SetGenerationResult = await res.json()
      setResult(data)

      const blob = base64ToBlob(data.zip_base64, 'application/zip')
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = data.zip_filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      opts.refreshDocCounter()
      // OR/DA-nummers leegmaken zodat de volgende set verse nummers krijgt
      opts.setForm(f => ({ ...f, order_response_number: '', desadv_number: '' }))
      prefillSetNumbers(true)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('error_injection.error_generate_failed'))
    } finally {
      setLoading(false)
    }
  }

  return {
    enabled, setEnabled,
    injections, setInjections, addInjection, removeInjection, updateInjection,
    result, clearResult: () => setResult(null),
    loading,
    error, setError,
    generateSet,
  }
}
